import React, { useState, useEffect, useContext } from "react";
import { getApiUrl } from "../config/api";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";

function AdminUsers() {
  const { user, token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "info",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "secretary",
    active: true,
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl("/api/admin/users"), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUsers(data.users);
        } else {
          showAlert("Erro ao carregar usuários", "error");
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
        showAlert("Erro ao carregar usuários", "error");
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === "admin") {
      fetchUsers();
    }
  }, [user, token]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("/api/admin/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        showAlert("Erro ao carregar usuários", "error");
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      showAlert("Erro ao carregar usuários", "error");
    } finally {
      setLoading(false);
    }
  };

  // Verificar se é admin
  if (!user || user.role !== "admin") {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Acesso negado. Esta página é apenas para administradores.
        </Alert>
      </Box>
    );
  }

  const showAlert = (message, type = "info") => {
    setAlert({ open: true, message, type });
    setTimeout(
      () => setAlert({ open: false, message: "", type: "info" }),
      5000,
    );
  };

  const handleOpenDialog = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setFormData({
        name: userToEdit.name,
        email: userToEdit.email,
        password: "",
        role: userToEdit.role,
        active: userToEdit.active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "secretary",
        active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "secretary",
      active: true,
    });
  };

  const handleInputChange = (field) => (event) => {
    if (field === "active") {
      setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    }
  };

  const handleSubmit = async () => {
    try {
      const method = editingUser ? "PUT" : "POST";
      const body = editingUser
        ? { userId: editingUser._id, ...formData }
        : formData;

      const response = await fetch(getApiUrl("/api/admin/users"), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        handleCloseDialog();
        fetchUsers();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao salvar usuário", "error");
      }
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);
      showAlert("Erro ao salvar usuário", "error");
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await fetch(getApiUrl("/api/admin/users"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId: userToDelete._id }),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        fetchUsers();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao remover usuário", "error");
      }
    } catch (error) {
      console.error("Erro ao remover usuário:", error);
      showAlert("Erro ao remover usuário", "error");
    }
  };

  const getRoleLabel = (role) => {
    return role === "admin" ? "Administrador" : "Secretária";
  };

  const getRoleColor = (role) => {
    return role === "admin" ? "primary" : "default";
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <InternalLayout>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100vh",
          pt: { xs: "56px", sm: "64px" },
          pb: { xs: 2, md: 3 },
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {/* Alert */}
        {alert.open && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2 }}
            onClose={() => setAlert({ open: false, message: "", type: "info" })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 },
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1.5rem", md: "2.125rem" },
            }}
          >
            <PersonIcon sx={{ fontSize: { xs: 24, md: 32 } }} /> Gerenciar
            Usuários
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            color="primary"
            sx={{
              width: { xs: "100%", md: "auto" },
              py: { xs: 1, md: 1.5 },
            }}
          >
            Novo Usuário
          </Button>
        </Box>

        {/* Estatísticas */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
              >
                Total de Usuários
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: "1.75rem", md: "3rem" } }}
              >
                {users.length}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center", py: { xs: 1.5, md: 2 } }}>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
              >
                Administradores
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: "1.75rem", md: "3rem" } }}
              >
                {users.filter((u) => u.role === "admin").length}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center", py: { xs: 1.5, md: 2 } }}>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
              >
                Secretárias
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: "1.75rem", md: "3rem" } }}
              >
                {users.filter((u) => u.role === "secretary").length}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Tabela de usuários - Desktop */}
        {!isMobile && (
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Função</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Último Login
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((userItem) => (
                  <TableRow key={userItem._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {userItem.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{userItem.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(userItem.role)}
                        color={getRoleColor(userItem.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={userItem.active ? "Ativo" : "Inativo"}
                        color={userItem.active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {userItem.lastLogin
                        ? new Date(userItem.lastLogin).toLocaleDateString(
                            "pt-BR",
                          )
                        : "Nunca"}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          onClick={() => handleOpenDialog(userItem)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {userItem._id !== user.id && (
                          <IconButton
                            onClick={() => {
                              setUserToDelete(userItem);
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Cards para mobile */}
        {isMobile && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {users.map((userItem) => (
              <Card key={userItem._id} sx={{ p: 2 }}>
                <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: "1.1rem", fontWeight: 600, mb: 0.5 }}
                      >
                        {userItem.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {userItem.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => handleOpenDialog(userItem)}
                        color="primary"
                        size="small"
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {userItem._id !== user.id && (
                        <IconButton
                          onClick={() => {
                            setUserToDelete(userItem);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                          size="small"
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Função:
                      </Typography>
                      <Chip
                        label={getRoleLabel(userItem.role)}
                        color={getRoleColor(userItem.role)}
                        size="small"
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      <Chip
                        label={userItem.active ? "Ativo" : "Inativo"}
                        color={userItem.active ? "success" : "default"}
                        size="small"
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Último Login:
                      </Typography>
                      <Typography variant="body2">
                        {userItem.lastLogin
                          ? new Date(userItem.lastLogin).toLocaleDateString(
                              "pt-BR",
                            )
                          : "Nunca"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Dialog para criar/editar usuário */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{
            "& .MuiDialog-paper": {
              mx: { xs: 0, sm: 2 },
              my: { xs: 0, sm: 4 },
              maxHeight: { xs: "100vh", sm: "calc(100vh - 64px)" },
              borderRadius: { xs: 0, sm: 1 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            {editingUser ? "Editar Usuário" : "Novo Usuário"}
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, sm: 2.5 },
                mt: 1,
              }}
            >
              <TextField
                label="Nome"
                value={formData.name}
                onChange={handleInputChange("name")}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange("email")}
                fullWidth
                required
                disabled={!!editingUser}
              />
              <TextField
                label={
                  editingUser ? "Nova Senha (deixe vazio para manter)" : "Senha"
                }
                type="password"
                value={formData.password}
                onChange={handleInputChange("password")}
                fullWidth
                required={!editingUser}
                helperText="Mínimo 6 caracteres"
              />
              <FormControl fullWidth>
                <InputLabel>Função</InputLabel>
                <Select
                  value={formData.role}
                  onChange={handleInputChange("role")}
                  label="Função"
                >
                  <MenuItem value="secretary">Secretária</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleInputChange("active")}
                  />
                }
                label="Usuário ativo"
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              gap: { xs: 1, sm: 1 },
            }}
          >
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingUser ? "Salvar" : "Criar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação para deletar */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
          sx={{
            "& .MuiDialog-paper": {
              mx: { xs: 0, sm: 2 },
              my: { xs: 0, sm: 8 },
              borderRadius: { xs: 0, sm: 1 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>Confirmar Remoção</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography variant="body2">
              Tem certeza que deseja remover o usuário{" "}
              <strong>{userToDelete?.name}</strong>? Esta ação não pode ser
              desfeita.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              gap: { xs: 1, sm: 1 },
            }}
          >
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleDeleteUser}
              color="error"
              variant="contained"
            >
              Remover
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </InternalLayout>
  );
}

export default AdminUsers;
